// @ts-check

/**
 * This class is for making payments with the "Pay by Bank" payment method.
 * 
 * How this works:
 * - User selects their bank from Stripe's Payment Element
 * - Request sent to our server to create a PaymentIntent / donation record
 * - Server returns paymentIntentClientSecret
 * - confirmPayment (from Stripe JS SDK) is called, this opens a modal with the user's bank inside, here
 * the user completes the payment using either their browser or a QR code scanned through their phone.
 * - Once payment succeeds/fails (or the user closes the modal), they are redirected to /pay-by-bank-result,
 * this is done automatically by Stripe.
 * 
 * Stripe docs on this payment style: https://docs.stripe.com/payments/accept-a-payment-deferred?platform=web&type=payment
 */

import stripeStyles from "./stripe-styles";

const POST_PAYMENT_REDIRECT_URL = `${window.location.origin}/donate/pay-by-bank/result`
const START_PAYMENT_PATH = '/donate/pay-by-bank/start';

export default class PayByBank {

    /**
     * @typedef PayByBankConstructorParams
     * @prop {HTMLFormElement} donationFormElement
     * @prop {any} stripeJsSdk
     */

    /**
     * @param {PayByBankConstructorParams} params
     */
    constructor({ 
        // A reference to the donation form DOM element
        donationFormElement,
        // The same as Stripe(<api_key>)
        stripeJsSdk 
    }) {
        // @ts-ignore
        this.$container = $('.js-payment-element-pay-by-bank');
        // @ts-ignore
        this.$description = $('.js-pay-by-bank-desc');

        if (!this.shouldInitialise()) {
            console.log('Pay by Bank not initialised, payment method not enabled.');
            return;
        }

        this.stripeJsSdk = stripeJsSdk;
        this.isActivated = false;
        this.mounted = false;
        this.donationFormElement = donationFormElement;

        this.elements = this.createElements();
        this.paymentElement = this.createPaymentElement();
        this.bindEvents();
        this.mountPaymentElement();
    }

    shouldInitialise() {
        const { payByBankDisabled } = this.$container.data();
        return !payByBankDisabled;
    }

    bindEvents() {
        this.paymentElement.on('ready', () => {
            this.mounted = true
        });
    }

    /**
     * Reveals the payment UI and sets a flag indicating that this payment method was used.
     */
    activate() {
        this.$container.show();
        this.$description.show();
        this.isActivated = true;
    }

    /**
     * Hides the payment UI and sets a flag indicating that this payment method is no longer being used.
     */
    deactivate() {
        this.$container.hide();
        this.$description.hide();
        this.isActivated = false;
    }
    
    /**
     * Creates a PaymentIntent that can used to make a Pay by Bank payment.
     */
    async createPaymentIntent() {
        try {
            const resp = await fetch(START_PAYMENT_PATH, {
                method: 'POST',
                body: new FormData(this.donationFormElement)
            });

            const { paymentIntentClientSecret, success } = await resp.json();

            // All OK - we can make a payment from this
            if (success) {
                return {
                    paymentIntentClientSecret
                }
            }

            // Our server either:
            // - Failed to process the request
            // - Failed to create the PaymentIntent
            // The exact reason is irrelevant, we can't recover from this.
            return {
                error: 'Failed to create PaymentIntent'
            }
        } catch (error) {
            // This is unrecoverable
            return {
                error
            }
        }
    }

    async startPayment() {
        const { 
            paymentIntentClientSecret, 
            error: paymentIntentCreationError 
        } = await this.createPaymentIntent();

        // We can't make a payment without a PaymentIntent client secret.
        if (paymentIntentCreationError) {
            console.log('PaymentIntent creation failed.', paymentIntentCreationError);
            return {
                error: paymentIntentCreationError
            }
        }

        // This must be called before confirming payment - https://docs.stripe.com/js/elements/submit
        const { error: elementsSubmitError } = await this.elements.submit();

        if (elementsSubmitError) {
            console.log('Stripe Elements submit failed.', elementsSubmitError);
            return {
                error: elementsSubmitError
            }
        }

        // https://docs.stripe.com/js/payment_intents/confirm_payment
        return this.stripeJsSdk.confirmPayment({
            elements: this.elements,
            clientSecret: paymentIntentClientSecret,
            confirmParams: {
                return_url: POST_PAYMENT_REDIRECT_URL
            }
        });
    }

    createElements() {
        return this.stripeJsSdk.elements({
            mode: 'payment',
            // This makes no difference for Pay by Bank - the amount is taken from the form, not this
            // element.
            amount: 1000,
            currency: 'gbp',
            paymentMethodTypes: ['pay_by_bank'],
            fonts: stripeStyles.fonts,
            appearance: stripeStyles.paymentElementStyles
        });
    }

    createPaymentElement() {
        const options = {
            // This does nothing, as there's only 1 payment method.
            layout: 'tabs',
            // These shouldn't be shown, but make doubly sure they can't be
            wallets: {
                applePay: 'never',
                googlePay: 'never'
            }
        }

        return this.elements.create('payment', options);
    }

    mountPaymentElement() {
        this.paymentElement.mount(this.$container[0]);
    }
}